import React, { useState, useEffect } from 'react';
import { useTheme } from './context/ThemeContext';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import HomePage from './HomePage';
import ServicesPage from './ServicesPage';
import MetiersPage from './MetiersPage';
import ReferencesPage from './ReferencesPage';
import RecrutementPage from './RecrutementPage';
import SocietePage from './SocietePage';
import ContactPage from './ContactPage';
import HeroSlider from './components/HeroSlider';
import AuthModal from './components/AuthModal';
import SearchModal from './components/SearchModal';
import { useAuth } from './auth/AuthContext';
import pb from './lib/pocketbase';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AccountPage from './pages/AccountPage';
import AdminDashboard from './pages/AdminDashboard';
import HeroSlidesPage from './admin/HeroSlidesPage';
import { UserRoute, AdminRoute } from './auth/ProtectedRoutes';
import AdminLayout from './admin/AdminLayout';
import UsersPage from './admin/UsersPage';
import ReferencesListPage from './admin/ReferencesListPage';
import ReferenceEditPage from './admin/ReferenceEditPage';
import ReferenceDetailPage from './ReferenceDetailPage';
import MobileMenuAccordion from './components/MobileMenuAccordion';
import Footer from './components/Footer';
import ScrollToTopButton from './components/ScrollToTopButton';
import MentionsLegalesPage from './MentionsLegalesPage';
import ScrollToTop from './components/ScrollToTop';
import CookieConsent from './components/CookieConsent';
import ThemeChoiceModal from './components/ThemeChoiceModal';
import SeasonalEffects from './components/SeasonalEffects';
import AdminSiteEffectsPage from './admin/AdminSiteEffectsPage';
import AdminRecrutementManager from './admin/AdminRecrutementManager';
import AdminServicesManager from './admin/AdminServicesManager';
import AdminMetiersManager from './admin/AdminMetiersManager';
import AdminSocieteManager from './admin/AdminSocieteManager';
import AdminMessages from './admin/AdminMessages';
import AdminTechnologiesManager from './admin/AdminTechnologiesManager';
import AdminHighlightsManager from './admin/AdminHighlightsManager';
import AdminHomeManager from './admin/AdminHomeManager';
import AdminContactManager from './admin/AdminContactManager';
import AdminLegalManager from './admin/AdminLegalManager';
import TechnologiesPage from './TechnologiesPage';
import { useRecruitmentData } from './context/RecruitmentContext';
import { useServicesData } from './context/ServicesContext';
import { useMetiersData } from './context/MetiersContext';
import { useSocieteData } from './context/SocieteContext';
import { useReferencesData } from './context/ReferencesContext';
import { useTechnologiesData } from './context/TechnologiesContext';
import { useHighlightsData } from './context/HighlightsContext';
import { useContactData } from './context/ContactContext';
import { useHomeData } from './context/HomeContext';
import { useSiteSettings } from './context/SiteSettingsContext';
import GlobalSplashScreen from './components/GlobalSplashScreen';

// Main App Component
function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, setTheme, toggleTheme } = useTheme();
  const [showThemeChoice, setShowThemeChoice] = useState(false);
  const location = useLocation();
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { sections: homeSections, loading: loadingHome } = useHomeData();
  const { categories: recruitmentCats, sections: recruitmentSections, hero: recruitmentHero, loading: loadingRecruitment } = useRecruitmentData();
  const { categories: servicesCats, sections: servicesSections, loading: loadingServices } = useServicesData();
  const { categories: metiersCats, sections: metiersSections, loading: loadingMetiers } = useMetiersData();
  const { categories: societeCats, sections: societeSections, loading: loadingSociete } = useSocieteData();
  const { references, hero: referencesHero, loading: loadingReferences } = useReferencesData();
  const { loading: loadingTechnologies } = useTechnologiesData();
  const { loading: loadingHighlights } = useHighlightsData();
  const { loading: loadingContact } = useContactData();
  const { showContact } = useSiteSettings();

  const [globalLoading, setGlobalLoading] = useState(true);
  const [splashFinished, setSplashFinished] = useState(false);

  useEffect(() => {
    // Check if critical data is loaded
    if (!loadingRecruitment && !loadingServices && !loadingMetiers && !loadingSociete && !loadingReferences && !loadingTechnologies && !loadingHighlights && !loadingContact && !loadingHome) {
      // Data loaded successfully
      setGlobalLoading(false);
    }
  }, [loadingRecruitment, loadingServices, loadingMetiers, loadingSociete, loadingReferences, loadingTechnologies, loadingHighlights, loadingContact, loadingHome]);

  // Force splash screen to dismiss after a max timeout (e.g., 2s max total life)
  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalLoading(false);
    }, 1500); // Force exit after 1.5s regardless of data
    return () => clearTimeout(timer);
  }, []);

  const handleSplashComplete = () => {
    setSplashFinished(true);
  };
  // Compute route flags early so they are available to hooks below
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isRefDetailRoute = /^\/references\/.+/.test(location.pathname);


  // N'afficher la popup de thème que lors de la toute première visite
  useEffect(() => {
    const hasChosenTheme = localStorage.getItem('app-theme');
    if (!hasChosenTheme) {
      setShowThemeChoice(true);
    }
  }, []);


  // Global shortcut: Ctrl/Cmd + K to open search (only on public routes)
  useEffect(() => {
    const onKey = (e) => {
      // Re-evaluate against current pathname to avoid stale deps
      if (location.pathname.startsWith('/admin')) return;
      const isCmdK = (e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K');
      if (isCmdK) {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.pathname]);

  // Close mobile menu on route change to avoid click glitches and ensure instant feedback
  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsNavigating(false);
  }, [location.pathname]);

  // Smooth-scroll to hash after route change when coming from another page
  useEffect(() => {
    const supportsSmoothRoutes = ['/services', '/metiers', '/recrutement', '/societe'];
    if (supportsSmoothRoutes.includes(location.pathname) && location.hash) {
      const anchorId = location.hash.replace('#', '');
      // Defer to ensure the page content is rendered
      const t = setTimeout(() => {
        const el = document.getElementById(anchorId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
      return () => clearTimeout(t);
    }
  }, [location.pathname, location.hash]);

  const toggleMenu = () => {
    setIsMenuOpen((open) => !open);
  };

  const handleMetiersSubmenuAnchorClick = (e, anchorId) => {
    closeMenu();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    forceCloseDropdown();
    if (location.pathname === '/metiers') {
      e.preventDefault();
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.pushState({}, '', `#${anchorId}`);
      }
    }
  };

  const handleTopLevelMetiersClick = () => {
    closeMenu();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    forceCloseDropdown();
  };

  const handleRecrutementSubmenuAnchorClick = (e, anchorId) => {
    closeMenu();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    forceCloseDropdown();
    if (location.pathname === '/recrutement') {
      e.preventDefault();
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.pushState({}, '', `#${anchorId}`);
      }
    }
  };

  const handleTopLevelRecrutementClick = () => {
    closeMenu();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    forceCloseDropdown();
  };

  const handleSocieteSubmenuAnchorClick = (e, anchorId) => {
    closeMenu();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    forceCloseDropdown();
    if (location.pathname === '/societe') {
      e.preventDefault();
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.pushState({}, '', `#${anchorId}`);
      }
    }
  };

  const handleTopLevelSocieteClick = () => {
    closeMenu();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    forceCloseDropdown();
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };



  const handleChooseTheme = (choice) => {
    setTheme(choice === 'dark' ? 'dark' : 'light');
    setShowThemeChoice(false);
  };
  const handleCloseThemeChoice = () => {
    // keep current theme (default light) and close
    setShowThemeChoice(false);
  };
  const handlePreviewTheme = (choice) => {
    // Preview immediately without closing the modal
    setTheme(choice === 'dark' ? 'dark' : 'light');
  };

  const openAuth = () => setIsAuthOpen(true);
  const closeAuth = () => setIsAuthOpen(false);
  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);

  const forceCloseDropdown = () => {
    const navEl = document.querySelector('.nav');
    if (!navEl) return;
    navEl.classList.add('dropdown-force-close');
    // Remove the class shortly after to restore normal behavior
    setTimeout(() => navEl.classList.remove('dropdown-force-close'), 300);
  };

  const handleSubmenuAnchorClick = (e, anchorId) => {
    // Always close mobile menu state
    closeMenu();
    // Always blur to collapse any :focus-within dropdown immediately
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    // Also force close hover-based dropdown immediately
    forceCloseDropdown();
    // If already on /services, do smooth in-page scroll and keep SPA
    if (location.pathname === '/services') {
      e.preventDefault();
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Update URL hash without full navigation
        window.history.pushState({}, '', `#${anchorId}`);
      }
    }
  };

  const handleSubmenuContainerClick = () => {
    // Close when clicking anywhere inside the dropdown
    closeMenu();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    forceCloseDropdown();
  };

  const handleTopLevelServicesClick = () => {
    // Close menus and dropdown when clicking the main Services link
    closeMenu();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    forceCloseDropdown();
  };

  // Active path helper for desktop nav highlighting (accessible in JSX)
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Ensure mobile nav is closed on desktop sizes to avoid duplicate menus
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    // Run once on mount
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getInitials = (nameOrEmail) => {
    if (!nameOrEmail) return 'U';
    const str = String(nameOrEmail);
    const parts = str.includes(' ')
      ? str.split(' ').filter(Boolean)
      : str.split('@')[0].split('.').filter(Boolean);
    const first = parts[0]?.[0] || str[0];
    const second = parts[1]?.[0] || '';
    return (first + second).toUpperCase();
  };

  const avatarUrl = user?.avatar ? pb.files.getUrl(user, user.avatar, { thumb: '64x64' }) : null;

  const homeHeroSection = homeSections.find(s => s.type === 'hero_overlay');

  const pageTitles = {
    '/': homeHeroSection?.title || 'Accueil',
    '/services': 'Services',
    '/metiers': 'Métiers',
    '/references': referencesHero?.title || 'Références',
    '/recrutement': recruitmentHero?.slider_title || 'Recrutement',
    '/societe': 'Société',
    '/contact': 'Contact',
  };
  const pageTitle = pageTitles[location.pathname] || 'Version Net';
  const pageSubtitles = {
    '/': homeHeroSection?.subtitle || "ESN spécialisée en solutions web & mobile",
    '/services': 'Conseil, conception, développement, intégration, hébergement',
    '/metiers': 'Projets web métiers, GED & dématérialisation, mobilité',
    '/references': referencesHero?.subtitle || 'Ils nous font confiance pour leurs projets critiques',
    '/recrutement': recruitmentHero?.slider_subtitle || 'Rejoignez une équipe pluridisciplinaire et passionnée',
    '/societe': 'Depuis 20 ans, nous construisons des solutions durables',
    '/contact': 'Parlons de votre projet et de vos objectifs',
  };
  const pageSubtitle = pageSubtitles[location.pathname] || '';

  return (
    <div className="app">
      <ScrollToTop />
      <SeasonalEffects />
      {isNavigating && <div className="route-progress" />}
      {!isAdminRoute && (
        <header className="header">
          <div className="header-inner">
            <Link to="/" className="logo" onClick={(e) => {
              closeMenu();
              if (location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}>
              <img src="/images/logo.png" alt="Version Net" className="logo-img" />
            </Link>
            <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
              <button
                className="nav-toggle"
                aria-label="Ouvrir le menu"
                onClick={toggleMenu}
              >
                <span className="nav-toggle-bar" />
                <span className="nav-toggle-bar" />
                <span className="nav-toggle-bar" />
              </button>
              <ul className="nav-list">
                {/* Mobile drawer logo */}
                {isMenuOpen && (
                  <div className="drawer-logo">
                    <img src="/images/logo.png" alt="Version Net" className="drawer-logo-img" />
                  </div>
                )}
                {/* Mobile drawer search trigger */}
                {isMenuOpen && (
                  <div className="drawer-search">
                    <button
                      type="button"
                      className="drawer-search-button"
                      aria-label="Rechercher"
                      onClick={() => { setIsSearchOpen(true); setIsMenuOpen(false); }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M15.5 15.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      <span>Recherche</span>
                    </button>
                  </div>
                )}
                {isMenuOpen && (
                  <div className="drawer-theme">
                    <button
                      type="button"
                      className="drawer-theme-button"
                      aria-label="Changer de thème"
                      onClick={toggleTheme}
                    >
                      {theme === 'dark' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="4" />
                          <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l-1.4-1.4M20.4 20.4 19 19M5 19l-1.4 1.4M20.4 3.6 19 5" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                        </svg>
                      )}
                      <span>Thème</span>
                    </button>
                  </div>
                )}
                {isMenuOpen && (
                  <MobileMenuAccordion onNavigate={closeMenu} />
                )}
                <li className="nav-item" onClick={closeMenu}>
                  <Link to="/" className={isActive('/') ? 'active' : ''}>Accueil</Link>
                </li>
                <li className="nav-item has-submenu" onClick={closeMenu}>
                  <Link to="/services" className={isActive('/services') ? 'active' : ''} onClick={handleTopLevelServicesClick}>Services</Link>
                  <ul className="submenu" onClick={handleSubmenuContainerClick}>
                    {servicesCats.length > 0 ? (
                      servicesCats.map(cat => {
                        const catSections = servicesSections
                          .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                          .filter(s => s.is_published !== false)
                          .sort((a, b) => (a.order || 0) - (b.order || 0));

                        return (
                          <li key={cat.id} className="submenu-col">
                            <Link
                              to={`/services#${cat.slug}`}
                              className="submenu-title"
                              onClick={(e) => handleSubmenuAnchorClick(e, cat.slug)}
                            >
                              {cat.title}
                            </Link>
                            {catSections.length > 0 && (
                              <ul>
                                {catSections.map(s => (
                                  <li key={s.id}>
                                    <Link
                                      to={`/services#${s.slug}`}
                                      onClick={(e) => handleSubmenuAnchorClick(e, s.slug)}
                                    >
                                      {s.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })
                    ) : (
                      <li className="submenu-col">
                        <Link to="/services#service" className="submenu-title" onClick={(e) => handleSubmenuAnchorClick(e, 'service')}>Services</Link>
                        <ul>
                          <li><Link to="/services#conseil-details" onClick={(e) => handleSubmenuAnchorClick(e, 'conseil-details')}>Conseil</Link></li>
                          <li><Link to="/services#realisation-details" onClick={(e) => handleSubmenuAnchorClick(e, 'realisation-details')}>Réalisation & intégration</Link></li>
                          <li><Link to="/services#maintenance-details" onClick={(e) => handleSubmenuAnchorClick(e, 'maintenance-details')}>Maintenance & infogérance</Link></li>
                        </ul>
                      </li>
                    )}
                  </ul>
                </li>
                <li className="nav-item has-submenu" onClick={closeMenu}>
                  <Link to="/metiers" className={isActive('/metiers') ? 'active' : ''} onClick={handleTopLevelMetiersClick}>Métiers</Link>
                  <ul className="submenu" onClick={handleSubmenuContainerClick}>
                    {metiersCats.length > 0 ? (
                      metiersCats.map(cat => {
                        const catSections = metiersSections
                          .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                          .filter(s => s.is_published !== false)
                          .sort((a, b) => (a.order || 0) - (b.order || 0));

                        return (
                          <li key={cat.id} className="submenu-col">
                            <Link
                              to={`/metiers#${cat.slug}`}
                              className="submenu-title"
                              onClick={(e) => handleMetiersSubmenuAnchorClick(e, cat.slug)}
                            >
                              {cat.title}
                            </Link>
                            {catSections.length > 0 && (
                              <ul>
                                {catSections.map(s => (
                                  <li key={s.id}>
                                    <Link
                                      to={`/metiers#${s.slug}`}
                                      onClick={(e) => handleMetiersSubmenuAnchorClick(e, s.slug)}
                                    >
                                      {s.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })
                    ) : (
                      <>
                        <li className="submenu-col">
                          <Link to="/metiers#metiers-overview" className="submenu-title" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'metiers-overview')}>Métiers</Link>
                          <ul>
                            <li><Link to="/metiers#architecture" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'architecture')}>Architecture</Link></li>
                            <li><Link to="/metiers#developpement" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'developpement')}>Développement</Link></li>
                            <li><Link to="/metiers#reseau" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'reseau')}>Réseau, exploitation</Link></li>
                            <li><Link to="/metiers#design" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'design')}>Design & ergonomie</Link></li>
                            <li><Link to="/metiers#content" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'content')}>Content management</Link></li>
                            <li><Link to="/metiers#ged-metiers" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'ged-metiers')}>Gestion Electronique des Documents</Link></li>
                            <li><Link to="/metiers#mobilite-metiers" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'mobilite-metiers')}>Mobilité</Link></li>
                            <li><Link to="/metiers#accessibilite" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'accessibilite')}>Accessibilité</Link></li>
                          </ul>
                        </li>
                        <li className="submenu-col">
                          <Link to="/metiers#expertise" className="submenu-title" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'expertise')}>Expertise technique</Link>
                          <ul>
                            <li><Link to="/metiers#expertise-mobilite" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'expertise-mobilite')}>Mobilité</Link></li>
                            <li><Link to="/metiers#dotnet" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'dotnet')}>.NET</Link></li>
                            <li><Link to="/metiers#php" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'php')}>PHP</Link></li>
                            <li><Link to="/metiers#java" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'java')}>Java/J2EE</Link></li>
                            <li><Link to="/metiers#coldfusion" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'coldfusion')}>ColdFusion</Link></li>
                            <li><Link to="/metiers#xml" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'xml')}>XML</Link></li>
                            <li><Link to="/metiers#elo-expertise" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'elo-expertise')}>Solutions GED ELO Digital</Link></li>
                            <li><Link to="/metiers#panorama" onClick={(e) => handleMetiersSubmenuAnchorClick(e, 'panorama')}>Panorama technique</Link></li>
                          </ul>
                        </li>
                      </>
                    )}
                  </ul>
                </li>
                <li className="nav-item has-submenu" onClick={closeMenu}>
                  <Link to="/recrutement" className={isActive('/recrutement') ? 'active' : ''} onClick={handleTopLevelRecrutementClick}>Recrutement</Link>
                  <ul className="submenu" onClick={handleSubmenuContainerClick}>
                    {recruitmentCats.length > 0 ? (
                      recruitmentCats.map(cat => {
                        const catSections = recruitmentSections
                          .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                          .filter(s => s.is_published !== false)
                          .sort((a, b) => (a.order || 0) - (b.order || 0));

                        return (
                          <li key={cat.id} className="submenu-col">
                            <Link
                              to={`/recrutement#${cat.slug}`}
                              className="submenu-title"
                              onClick={(e) => handleRecrutementSubmenuAnchorClick(e, cat.slug)}
                            >
                              {cat.title}
                            </Link>
                            {catSections.length > 0 && (
                              <ul>
                                {catSections.map(s => (
                                  <li key={s.id}>
                                    <Link
                                      to={`/recrutement#${s.slug}`}
                                      onClick={(e) => handleRecrutementSubmenuAnchorClick(e, s.slug)}
                                    >
                                      {s.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })
                    ) : (
                      <li className="submenu-col">
                        <Link to="/recrutement" className="submenu-title">Recrutement</Link>
                        <ul>
                          {showContact && <li><Link to="/contact">Postuler</Link></li>}
                          <li><Link to="/recrutement">Voir nos offres</Link></li>
                        </ul>
                      </li>
                    )}
                  </ul>
                </li>
                <li className="nav-item" onClick={closeMenu}>
                  <Link to="/references" className={isActive('/references') ? 'active' : ''}>Références</Link>
                </li>
                <li className="nav-item has-submenu" onClick={closeMenu}>
                  <Link to="/societe" className={isActive('/societe') ? 'active' : ''} onClick={handleTopLevelSocieteClick}>Société</Link>
                  <ul className="submenu" onClick={handleSubmenuContainerClick}>
                    {societeCats.length > 0 ? (
                      societeCats.map(cat => {
                        const catSections = societeSections
                          .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                          .filter(s => s.is_published !== false)
                          .sort((a, b) => (a.order || 0) - (b.order || 0));

                        return (
                          <li key={cat.id} className="submenu-col">
                            <Link
                              to={`/societe#${cat.slug}`}
                              className="submenu-title"
                              onClick={(e) => handleSocieteSubmenuAnchorClick(e, cat.slug)}
                            >
                              {cat.title}
                            </Link>
                            {catSections.length > 0 && (
                              <ul>
                                {catSections.map(s => (
                                  <li key={s.id}>
                                    <Link
                                      to={`/societe#${s.slug}`}
                                      onClick={(e) => handleSocieteSubmenuAnchorClick(e, s.slug)}
                                    >
                                      {s.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })
                    ) : (
                      <li className="submenu-col">
                        <Link to="/societe#qui" className="submenu-title" onClick={(e) => handleSocieteSubmenuAnchorClick(e, 'qui')}>Qui sommes-nous ?</Link>
                        <ul>
                          <li><Link to="/societe#ou" onClick={(e) => handleSocieteSubmenuAnchorClick(e, 'ou')}>Où sommes-nous ?</Link></li>
                          <li><Link to="/societe#contact" onClick={(e) => handleSocieteSubmenuAnchorClick(e, 'contact')}>Nous contacter</Link></li>
                        </ul>
                      </li>
                    )}
                  </ul>
                </li>
                {showContact && (
                  <li className="nav-item" onClick={closeMenu}>
                    <Link to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact</Link>
                  </li>
                )}
              </ul>
            </nav>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label="Changer de thème"
              type="button"
            >
              <span className="theme-toggle-track">
                <span className="theme-toggle-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l-1.4-1.4M20.4 20.4 19 19M5 19l-1.4 1.4M20.4 3.6 19 5" />
                  </svg>
                </span>
                <span className="theme-toggle-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                </span>
                <span
                  className={`theme-toggle-thumb ${theme === 'dark' ? 'theme-toggle-thumb-left' : 'theme-toggle-thumb-right'
                    }`}
                />
              </span>
            </button>
            <div className="header-actions">
              <button
                className="search-button"
                aria-label="Rechercher"
                type="button"
                onClick={openSearch}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-3.6-3.6" />
                </svg>
              </button>
              <button
                className="avatar-button"
                aria-label="Ouvrir la fenêtre de connexion"
                type="button"
                onClick={openAuth}
              >
                <span className="avatar-circle">
                  {isAuthenticated ? (
                    avatarUrl ? (
                      <img className="avatar-img" src={avatarUrl} alt="Avatar" />
                    ) : (
                      <span className="avatar-initials">{getInitials(user?.name || user?.email)}</span>
                    )
                  ) : (
                    <UserIcon />
                  )}
                </span>
              </button>
            </div>
          </div>
        </header>
      )}
      {isMenuOpen && !isAdminRoute && (
        <div className="nav-backdrop" onClick={closeMenu} aria-hidden="true" />
      )}
      {isAuthOpen && !isAdminRoute && <AuthModal onClose={closeAuth} />}
      {isSearchOpen && !isAdminRoute && <SearchModal onClose={closeSearch} />}
      {!isAdminRoute && !isRefDetailRoute && (
        <HeroSlider pageTitle={pageTitle} pageSubtitle={pageSubtitle} />
      )}
      <main style={isAdminRoute ? { paddingTop: 0 } : undefined}>
        <Routes key={location.pathname}>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/metiers" element={<MetiersPage />} />
          <Route path="/references" element={<ReferencesPage />} />
          <Route path="/references/:slug" element={<ReferenceDetailPage />} />
          <Route path="/recrutement" element={<RecrutementPage />} />
          <Route path="/societe" element={<SocietePage />} />
          <Route path="/technologies" element={<TechnologiesPage />} />
          <Route path="/contact" element={showContact ? <ContactPage /> : <Navigate to="/" replace />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          {/* Auth public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* Protected user routes */}
          <Route element={<UserRoute />}>
            <Route path="/mon-compte" element={<AccountPage />} />
          </Route>
          {/* Protected admin routes */}
          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route index path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/references" element={<ReferencesListPage />} />
              <Route path="/admin/hero-slides" element={<HeroSlidesPage />} />
              <Route path="/admin/references/new" element={<ReferenceEditPage />} />
              <Route path="/admin/references/:id" element={<ReferenceEditPage />} />
              <Route path="/admin/effets" element={<AdminSiteEffectsPage />} />
              <Route path="/admin/metiers" element={<AdminMetiersManager />} />
              <Route path="/admin/societe" element={<AdminSocieteManager />} />
              <Route path="/admin/recrutement" element={<AdminRecrutementManager />} />
              <Route path="/admin/services" element={<AdminServicesManager />} />
              <Route path="/admin/messages" element={<AdminMessages />} />
              <Route path="/admin/technologies" element={<AdminTechnologiesManager />} />
              <Route path="/admin/highlights" element={<AdminHighlightsManager />} />
              <Route path="/admin/home" element={<AdminHomeManager />} />
              <Route path="/admin/contact" element={<AdminContactManager />} />
              <Route path="/admin/legal" element={<AdminLegalManager />} />
            </Route>
          </Route>
        </Routes>
      </main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <ScrollToTopButton />}
      {/* Theme choice modal on first visit */}
      {!isAdminRoute && (
        <ThemeChoiceModal
          open={showThemeChoice}
          onChoose={handleChooseTheme}
          onClose={handleCloseThemeChoice}
          onPreview={handlePreviewTheme}
        />
      )}


      <CookieConsent />
      {!splashFinished && <GlobalSplashScreen finished={!globalLoading} onComplete={handleSplashComplete} />}
    </div>
  );
}

export default App;

function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21a8 8 0 10-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
